import { useCallback, useEffect, useRef } from 'react'

import { useLightMode } from '@/hooks/useLightMode'
import { useWindowSize } from '@react-hook/window-size'

import { Box as DarkBox, tokenVar, useEscapeKey } from '@hivewatch/dark'

import { IncidentQueryResult } from '@/graphql/generated/hooks'
import { IncidentStatus } from '@/graphql/generated/schemas'
import { useAppDispatch, useAppSelector } from '@/redux/hooks'
import {
  closeIncidentMapPanelDrawer,
  selectIncidentMapPanelDrawerIsOpen,
} from '@/redux/ui/uiSlice'
import { trackEvent } from '@/utils/analytics'

import { MAP_PANEL_DRAWER_CLOSE } from '../../../shared/constants/analyticsEvents'
import {
  MAP_PANEL_DRAWER_BREAKPOINT,
  MAP_PANEL_DRAWER_WIDTH,
} from '../../../shared/constants/drawerDimensions'
import { getIncidentMapCoordinates } from '../../../shared/utils/mapCoordinates'
import { IncidentDrawerContainer } from '../../components/IncidentDrawerContainer'
import { MapPanelBody } from './MapPanelBody'
import { MapPanelHeader } from './MapPanelHeader'

interface MapPanelDrawerIProps {
  incident: IncidentQueryResult['data']['incident']
}

export const MapPanelDrawer = ({ incident }: MapPanelDrawerIProps) => {
  const isOpen = useAppSelector(selectIncidentMapPanelDrawerIsOpen)
  const dispatch = useAppDispatch()
  const mode = useLightMode()
  const isDark = mode === 'dark'
  const drawerBg = isDark ? tokenVar('colors.bg.card') : 'white'
  const [width] = useWindowSize()

  // Market Facility Incidents carry their own coordinates (the reporter's
  // manually entered coords, or the point resolved from the address they
  // entered) because the market facility has none on file; everything else
  // uses the facility's current coordinates. No snapshotting at incident time
  // for the facility case. ENG-33674
  const coordinates = getIncidentMapCoordinates(incident)
  const hasCoordinates = coordinates != null

  // The marker's alert coloring is driven by the incident in view, not by a
  // facility-wide count — the incident query carries none. An unresolved
  // incident means this facility has at least one active incident, which is
  // what the red state claims. Anything else — resolved, or a status that has
  // not arrived — tells us nothing, so the marker falls back to the resting
  // state rather than asserting an alert.
  const hasActiveIncident =
    incident?.status === IncidentStatus.Active ||
    incident?.status === IncidentStatus.InProgress

  // isOpen is a dep so programmatic reopens (e.g. the acknowledge rollback in
  // FeedButtons) re-validate the breakpoint even when the width last changed
  // while the panel was closed.
  useEffect(() => {
    if (width < MAP_PANEL_DRAWER_BREAKPOINT) {
      dispatch(closeIncidentMapPanelDrawer())
    }
  }, [width, isOpen])

  // Escape closes this sub-drawer only — the incident drawer behind it stays
  // open. Joins the dark package's escape stack so nesting resolves. The ref
  // scopes the foreign-dialog guard: a Chakra v1 dialog opened from the action
  // bar (Emergency Call) traps focus without joining the stack, and must keep
  // Escape rather than have it close this drawer behind it. ENG-32100
  const containerRef = useRef<HTMLDivElement>(null)
  const handleEscape = useCallback(() => {
    trackEvent(MAP_PANEL_DRAWER_CLOSE, {
      facility_id: incident?.facility?.id,
      incident_id: incident?.id,
      closed_via: 'escape_key',
    })
    dispatch(closeIncidentMapPanelDrawer())
  }, [dispatch, incident?.facility?.id, incident?.id])
  useEscapeKey(handleEscape, Boolean(isOpen && hasCoordinates), containerRef)

  if (!isOpen || !coordinates) return null

  const { latitude, longitude } = coordinates

  return (
    <IncidentDrawerContainer
      bgColor={drawerBg}
      borderColor={
        isDark
          ? tokenVar('colors.border.subtle')
          : tokenVar('colors.light.border.subtle')
      }
      containerRef={containerRef}
      dataTestid='MapPanelDrawer:wrapper'
      width={MAP_PANEL_DRAWER_WIDTH}
    >
      <MapPanelHeader
        mode={mode}
        onClose={() => {
          trackEvent(MAP_PANEL_DRAWER_CLOSE, {
            facility_id: incident?.facility?.id,
            incident_id: incident?.id,
            closed_via: 'close_button',
          })
          dispatch(closeIncidentMapPanelDrawer())
        }}
      />
      {/* Inset the map from the panel edges: the bordered, rounded frame keeps
          the canvas from running flush against the window top and bottom. */}
      <DarkBox flex='1' minHeight={0} pb='24px' px='24px'>
        <DarkBox
          border='1px solid'
          borderColor={isDark ? 'border.subtle' : 'light.border.subtle'}
          borderRadius='8px'
          height='100%'
          overflow='hidden'
        >
          {/* Keyed by incident, facility AND coordinates: initialViewState
              only applies once, so an incident switch, a facility switch or a
              coordinate edit remounts the map recentered, while ordinary
              polling with unchanged values keeps the user's pan/zoom. The
              incident id matters for Market Facility Incidents, which share
              one facility but each carry their own point. */}
          <MapPanelBody
            hasActiveIncident={hasActiveIncident}
            key={`${incident?.id}:${incident?.facility?.id}:${latitude}:${longitude}`}
            latitude={latitude}
            longitude={longitude}
            // Matches the Global Map's fallback for an unnamed facility
            // (mapbox/utils/clusters.ts), so the same facility never renders
            // labelled on one surface and blank on the other.
            shortName={incident?.facility?.shortName ?? 'N/A'}
          />
        </DarkBox>
      </DarkBox>
    </IncidentDrawerContainer>
  )
}
